/**
 * usePageSubscription — hook for subscribing to real-time page updates
 * via WebSocket. Displays a banner when the page is updated by another user,
 * and forwards page.lock.* soft-lock events to the editor.
 *
 * Reuses the shared websocket-client (createWebSocketClient) so page
 * subscriptions inherit the same reconnect/backoff/session-expiry behaviour as
 * the board path. The connection URL is resolved fresh on every connect attempt
 * (appConfig.wsUrl + pageId + a fresh auth token), mirroring BoardViewPage.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { appConfig } from '../../config';
import { useAuth } from '../../state/auth-context';
import { createWebSocketClient, type WebSocketClient } from '../../services/websocket-client';

export interface PageUpdateBanner {
  readonly visible: boolean;
  readonly updatedBy: string;
}

/** Payload shape for lock-related WebSocket events */
export interface LockEvent {
  readonly detailType: string;
  readonly payload: Record<string, unknown>;
}

interface UsePageSubscriptionOptions {
  readonly pageId: string | null;
  readonly hasUnsavedChanges: boolean;
  readonly onReload: () => void;
  /** Called when a page.lock.* event arrives via WebSocket */
  readonly onLockEvent?: (event: LockEvent) => void;
}

interface UsePageSubscriptionResult {
  readonly banner: PageUpdateBanner;
  readonly dismissBanner: () => void;
  readonly handleReload: () => void;
}

/**
 * Subscribe to page updates via WebSocket.
 * Shows a banner when the page is updated by another user.
 * If the editor has unsaved changes, shows the banner without overwriting.
 */
export function usePageSubscription({
  pageId,
  hasUnsavedChanges,
  onReload,
  onLockEvent,
}: UsePageSubscriptionOptions): UsePageSubscriptionResult {
  const { getAccessToken } = useAuth();
  const [banner, setBanner] = useState<PageUpdateBanner>({ visible: false, updatedBy: '' });
  const clientRef = useRef<WebSocketClient | null>(null);
  const hasUnsavedRef = useRef(hasUnsavedChanges);
  hasUnsavedRef.current = hasUnsavedChanges;
  const onLockEventRef = useRef(onLockEvent);
  onLockEventRef.current = onLockEvent;

  // Connect and subscribe when pageId changes
  useEffect(() => {
    if (!pageId) return;
    // Skip entirely if no WS URL is configured (backend not yet deployed).
    if (!appConfig.wsUrl) return;

    let cancelled = false;

    // Resolve a fresh WS URL (with a fresh, non-expired token) for each connect
    // attempt. The $connect authorizer requires both pageId and token; returning
    // null when the session can't be re-authenticated tells the client to stop
    // retrying instead of hot-looping reconnects.
    const getUrl = async (): Promise<string | null> => {
      const token = await getAccessToken();
      if (!token) return null;
      return `${appConfig.wsUrl}?pageId=${encodeURIComponent(pageId)}&token=${encodeURIComponent(token)}`;
    };

    const client = createWebSocketClient({
      boardId: pageId,
      getUrl,
      createSocket: (url: string) => new WebSocket(url),
      onConnect: (send) => {
        // Re-register the page subscription on every (re)connect.
        send(JSON.stringify({ action: 'subscribePage', pageId }));
      },
      onEvent: (data: Record<string, unknown>) => {
        if (cancelled) return;

        const payload = data.payload as Record<string, unknown> | undefined;

        // Soft-lock events — forward to the editor's useEditLock hook.
        if (
          typeof data.detailType === 'string' &&
          data.detailType.startsWith('page.lock.') &&
          payload?.pageId === pageId
        ) {
          onLockEventRef.current?.({ detailType: data.detailType, payload: payload ?? {} });
          return;
        }

        // Remote content update — show the "updated by {name}" banner.
        if (data.detailType === 'page.updated' && payload?.pageId === pageId) {
          const updatedByName =
            (payload.updatedByName as string) || (payload.updatedBy as string) || 'Someone';
          setBanner({ visible: true, updatedBy: updatedByName });
        }
      },
      onStateChange: () => {
        // Connection state is not surfaced for pages; reconnect is handled internally.
      },
    });

    clientRef.current = client;
    client.connect();

    // Cleanup: disconnect on unmount or pageId change.
    return () => {
      cancelled = true;
      if (clientRef.current) {
        clientRef.current.disconnect();
        clientRef.current = null;
      }
      setBanner({ visible: false, updatedBy: '' });
    };
  }, [pageId, getAccessToken]);

  const dismissBanner = useCallback(() => {
    setBanner({ visible: false, updatedBy: '' });
  }, []);

  const handleReload = useCallback(() => {
    setBanner({ visible: false, updatedBy: '' });
    onReload();
  }, [onReload]);

  return { banner, dismissBanner, handleReload };
}
